import {
  Link,
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSubmenu,
  SidebarSubmenuItem,
  useSidebarOpenState,
} from '@backstage/core-components';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';

import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';
import BookIcon from '@material-ui/icons/Book';
import BuildIcon from '@material-ui/icons/Build';
import CategoryIcon from '@material-ui/icons/Category';
import ChatBubbleIcon from '@material-ui/icons/ChatBubble';
import BarChartIcon from '@material-ui/icons/BarChart';
import HomeIcon from '@material-ui/icons/Home';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import StorefrontIcon from '@material-ui/icons/Storefront';
import { PropsWithChildren } from 'react';
// import { Banner } from '../alert/Banner';
import { parse, stringify } from 'query-string';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { triggerDynatraceEvent } from '../../utils/trigger-dynatrace-events';
import {
  IdentityApi,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const insightsActionsText = (
  <Typography
    variant="subtitle2"
    component="span"
    style={{
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      lineHeight: 'auto',
      flex: '3 1 auto',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'inherit',
    }}
  >
    <span>
      Insights and
      <br />
      actions
    </span>
  </Typography>
);

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const handleSearchEvent = () => {
  const eventType: any = {
    search_page_type: {
      value: 'SidebarSearchModal',
      public: true,
    },
  };
  triggerDynatraceEvent(eventType, 'search_page_event');
};

const setUserIdCookie = async (identity: IdentityApi) => {
  try {
    const cookies = document.cookie;
    if (cookies.split(';').some(c => c.trim().startsWith('userId='))) {
      // userId cookie already set
      return 'already-set';
    }
    const { displayName } = await identity.getProfileInfo();
    const userIdCookie = `userId=${displayName ?? ''}`;

    document.cookie = `${userIdCookie}; Secure; Path=/`;

    // make sure browser is refreshed to reflect the newly set meta tag in dynatrace
    window.location.reload();
    return 'success';
  } catch (_err) {
    // Ignore for now
    return 'failure';
  }
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const identity = useApi(identityApiRef);
  useAsync(async (): Promise<string> => {
    const userIdCookie = await setUserIdCookie(identity);
    return userIdCookie;
  }, []);
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarItem
          icon={SearchIcon}
          to="/search"
          text="Search"
          onClick={handleSearchEvent}
        />
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />
          <SidebarItem
            icon={BookIcon}
            to="/docs/principalfinancialgroup-emu/component/principal-engineering-handbook"
            text="Handbook"
          />

          <SidebarItem
            icon={BarChartIcon}
            // @ts-expect-error SidebarItem expects text to be string, but works with a ReactNode
            text={insightsActionsText}
          >
            <SidebarSubmenu title="Insights and actions">
              <SidebarSubmenuItem title="Insights" to="/insights" />
              <SidebarSubmenuItem title="Actions" to="/actions" />
            </SidebarSubmenu>
          </SidebarItem>

          <SidebarItem icon={CategoryIcon} text="Catalog">
            {' '}
            <SidebarSubmenu title="Catalog">
              <SidebarSubmenuItem
                title="Components"
                to="catalog?filters[kind]=component&filters[user]=owned"
              />
              <SidebarSubmenuItem
                title="Cloud Accounts"
                to="catalog?filters[kind]=resource&filters[type]=account&filters[user]=owned"
              />
              <SidebarSubmenuItem
                title="Templates"
                to="catalog?filters[kind]=template&filters[user]=all"
              />
              <SidebarSubmenuItem
                title="APIs"
                to="catalog?filters[kind]=api&filters[user]=owned"
              />
              <SidebarSubmenuItem
                title="Owning Teams"
                to="catalog?filters[kind]=group"
              />
              <SidebarSubmenuItem
                title="Users"
                to="catalog?filters[kind]=user"
              />
              <SidebarSubmenuItem title="Documentation" to="docs" />
            </SidebarSubmenu>{' '}
          </SidebarItem>
          <SidebarItem
            icon={StorefrontIcon}
            to="marketplace"
            text="Marketplace"
          />
          <SidebarItem icon={BuildIcon} to="toolbox" text="Toolbox" />
          {/* End global nav */}
        </SidebarGroup>
        <SidebarGroup>
          <div
            style={{
              position: 'relative',
              bottom: 0,
              top: 150,
              overflowY: 'scroll',
            }}
          >
            <SidebarDivider />
            <SidebarItem
              icon={ChatBubbleIcon}
              to="https://forms.office.com/r/4ghG5jFh5w"
              text="PDP feedback"
              target="_blank"
            />
            <SidebarGroup
              label="Settings"
              icon={<UserSettingsSignInAvatar />}
              to="/settings"
            >
              <SidebarSettings />
            </SidebarGroup>
          </div>
        </SidebarGroup>
      </Sidebar>
      {/* <Banner /> */}
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={{
          searchStringToObject: parse,
          objectToSearchString: stringify,
        }}
      >
        {children}
      </QueryParamProvider>
    </SidebarPage>
  );
};
